import React, { useCallback, useEffect, useState } from 'react';

import {
  AssignmentStatusTypeEnum,
  CompanySelectModel,
  CustomerSearchResponseModel,
} from 'api/adminApiClient';
import { AssignmentFilterTypes } from '../enums';
import CompanyFilter from 'components/filters/CompanyFilter';
import {
  getAssignmentsCompanies,
  getCustomersByAssignments,
  getWorkersByAssignments,
} from 'redux/reducers/assignmentSlice';
import CustomerFilter from 'components/filters/CustomerFilter';
import WorkerFilter from 'components/filters/WorkerFilter';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { isCorporateTASelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { WorkerSearchResponseModel } from 'api/backofficeApiClient';
import AssignmentTerminationReasonFilter from 'components/filters/AssignmentTerminationReasonFilter';
import AssignmentStatusFilter from 'components/filters/AssignmentStatusFilter';

import styles from '../../client/ClientRequestsSection/ClientRequestsSection.module.less';

export interface AssignmentFilters {
  [AssignmentFilterTypes.Company]?: string[];
  [AssignmentFilterTypes.Customer]?: string[];
  [AssignmentFilterTypes.Worker]?: string[];
  [AssignmentFilterTypes.TerminationReason]?: number[];
  [AssignmentFilterTypes.Status]?: AssignmentStatusTypeEnum[];
}

type Props = {
  filters: AssignmentFilters;
  assignmentStatuses: AssignmentStatusTypeEnum[];
  onChange: (data: AssignmentFilters) => void;
  isPendingSection?: boolean;
};

export default function AssignmentsFilter(props: Props) {
  const { filters, onChange, assignmentStatuses, isPendingSection } = props;
  const isAdmin = useAppSelector(isAdminSelector);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const [companies, setCompanies] = useState<CompanySelectModel[]>([]);
  const [isCompaniesLoaded, setIsCompaniesLoaded] = useState(false);
  const [customers, setCustomers] = useState<CustomerSearchResponseModel[]>([]);
  const [isCustomersLoaded, setIsCustomersLoaded] = useState(false);
  const [workers, setWorkers] = useState<WorkerSearchResponseModel[]>([]);
  const [isWorkersLoaded, setIsWorkersLoaded] = useState(false);

  const dispatch = useAppDispatch();

  const getClassName = useCallback(() => {
    if (isPendingSection) return styles.colSm;

    if (isAdmin) return styles.colXs;

    if (isCorporateTA) return styles.colSm;

    return styles.colXs;
  }, [isPendingSection, isAdmin, isCorporateTA]);



  const loadCompanies = useCallback(async () => {
    const result = await dispatch(getAssignmentsCompanies(assignmentStatuses));

    if (getAssignmentsCompanies.fulfilled.match(result)) {
      setCompanies(result.payload);
    }
    setIsCompaniesLoaded(true);
  }, [dispatch, assignmentStatuses]);

  const loadCustomers = useCallback(async () => {
    const result = await dispatch(
      getCustomersByAssignments({
        assignmentStatuses: assignmentStatuses,
        isAdminRole: isAdmin,
      }),
    );

    if (getCustomersByAssignments.fulfilled.match(result)) {
      setCustomers(result.payload);
    }
    setIsCustomersLoaded(true);
  }, [dispatch, isAdmin, assignmentStatuses]);

  const loadWorkers = useCallback(async () => {
    const result = await dispatch(getWorkersByAssignments(isAdmin as boolean));

    if (getWorkersByAssignments.fulfilled.match(result)) {
      setWorkers(result.payload);
    }
    setIsWorkersLoaded(true);
  }, [dispatch, isAdmin]);

  const loadData = useCallback(async () => {
    let requests = [];

    filters.hasOwnProperty(AssignmentFilterTypes.Company) &&
      requests.push(loadCompanies());
    filters.hasOwnProperty(AssignmentFilterTypes.Customer) &&
      requests.push(loadCustomers());
    filters.hasOwnProperty(AssignmentFilterTypes.Worker) &&
      requests.push(loadWorkers());

    await Promise.allSettled(requests);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCompanies, loadCustomers, loadWorkers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <>
      {filters.hasOwnProperty(AssignmentFilterTypes.Company) && (
        <div className={getClassName()}>
          <CompanyFilter
            filters={filters}
            onChange={onChange}
            companies={companies}
            isCompaniesLoaded={isCompaniesLoaded}
          />
        </div>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.Customer) && (
        <div className={getClassName()}>
          <CustomerFilter
            filters={filters}
            customers={customers}
            onChange={onChange}
            isCustomersLoaded={isCustomersLoaded}
          />
        </div>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.Worker) && (
        <div className={getClassName()}>
          <WorkerFilter
            filters={filters}
            workers={workers}
            onChange={onChange}
            isWorkersLoaded={isWorkersLoaded}
          />
        </div>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.TerminationReason) && (
        <div className={getClassName()}>
          <AssignmentTerminationReasonFilter
            filters={filters}
            onChange={onChange}
          />
        </div>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.Status) && (
        <div className={getClassName()}>
          <AssignmentStatusFilter filters={filters} onChange={onChange} />
        </div>
      )}
    </>
  );
}
